import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/synapse/Icon';
import { useHasPermission } from '@/hooks/usePermissions';
import { TENANT } from '@/routing/tenant';
import {
  useAptCounter,
  useDecrementCounter,
  useIncrementCounter,
} from '@/features/apt-counter/useAptCounter';
import {
  APT_TICKETS_KEY,
  useAptTickets,
  type AptTicketListItem,
} from '@/features/apt-counter/useAptTickets';
import { useCounterSocket } from '@/features/apt-counter/useCounterSocket';
import { VisitHistoryList } from '@/features/consulting/VisitHistoryList';
import { ageFromDob, sexLabelKey } from '@/features/patients/patientDisplay';

// How often to re-read today's queue while the page is open, on top of the
// refresh every counter change triggers. It catches the patient who is given
// the number that is ALREADY on the display — nothing about the counter changes
// when that happens.
const QUEUE_REFRESH_MS = 15_000;

/**
 * The doctor's screen during a session: the number being served, who holds it,
 * and that patient's past visits. Read-only — the doctor writes the visit on
 * paper and staff record it.
 *
 * The number follows the counter live, so pressing Next at the front desk (or
 * here) changes the patient without a reload.
 */
export function ConsultingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canReadCounter = useHasPermission('apt_counter.read');
  const canDriveCounter = useHasPermission('apt_counter.manage');

  const counterQ = useAptCounter();
  const live = useCounterSocket(TENANT.slug);
  const ticketsQ = useAptTickets({ refetchInterval: QUEUE_REFRESH_MS });
  const next = useIncrementCounter();
  const previous = useDecrementCounter();

  // The live stream leads; the plain read covers the moment before it connects
  // and the case where it cannot.
  const currentNumber = live.state?.currentNumber ?? counterQ.data?.currentNumber;
  const doctorIn = live.state?.doctorIn ?? counterQ.data?.doctorIn ?? false;

  // A new number may belong to a patient registered since the queue was last
  // read, so the queue is re-read whenever the number moves.
  useEffect(() => {
    if (currentNumber === undefined) return;
    void qc.invalidateQueries({ queryKey: APT_TICKETS_KEY });
  }, [currentNumber, qc]);

  if (!canReadCounter) {
    return (
      <Shell>
        <Message title={t('consulting.noCounterAccessTitle')} body={t('consulting.noCounterAccessBody')} />
      </Shell>
    );
  }

  if (currentNumber === undefined) {
    return (
      <Shell>
        <Message
          title={counterQ.isError ? t('aptCounter.couldNotLoad') : t('common.loading')}
          error={counterQ.isError}
        />
      </Shell>
    );
  }

  // A cancelled ticket still carries its number, but nobody is holding it.
  const ticket = ticketsQ.data?.tickets.find(
    (x) => x.number === currentNumber && x.status !== 'cancelled',
  );
  const busy = next.isPending || previous.isPending;

  return (
    <Shell>
      <Card className="flex flex-wrap items-center gap-x-6 gap-y-4 p-5">
        <div className="flex items-center gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
              {t('aptCounter.nowServing')}
            </div>
            <div
              aria-live="polite"
              aria-label={t('aptCounter.numberLabel', { number: currentNumber })}
              className="font-mono text-[56px] font-bold leading-none tabular-nums tracking-tight"
            >
              {currentNumber}
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Badge tone={doctorIn ? 'success' : 'neutral'} size="lg">
            {doctorIn ? t('aptCounter.doctorIn') : t('aptCounter.doctorOut')}
          </Badge>
          {live.status === 'reconnecting' && (
            <span className="text-[12px] text-muted">{t('publicCounter.reconnecting')}</span>
          )}
        </div>
        {canDriveCounter && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => previous.mutate()}
              disabled={busy || currentNumber === 0}
            >
              <Icon name="chevron_left" size={16} /> {t('consulting.previous')}
            </Button>
            <Button onClick={() => next.mutate()} disabled={busy}>
              {t('consulting.nextPatient')} <Icon name="chevron_right" size={16} />
            </Button>
          </div>
        )}
      </Card>

      {ticket ? (
        <CurrentPatient ticket={ticket} />
      ) : ticketsQ.isLoading ? (
        <Message title={t('common.loading')} />
      ) : ticketsQ.isError ? (
        <Message title={t('aptCounter.couldNotLoadQueue')} error />
      ) : (
        <Message
          title={t('consulting.nobodyTitle', { number: currentNumber })}
          body={t('consulting.nobodyBody')}
        />
      )}
    </Shell>
  );
}

function CurrentPatient({ ticket }: { ticket: AptTicketListItem }) {
  const { t } = useTranslation();
  const { patient } = ticket;
  const age = ageFromDob(patient.dateOfBirth);
  return (
    <>
      <Card className="flex flex-wrap items-center gap-x-4 gap-y-3 p-5">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[24px] font-bold leading-tight tracking-[-0.02em]">
            {patient.fullName}
          </h2>
          <div className="mt-1 text-[14px] text-muted">
            {age != null && (
              <>
                {age} {t('patients.yrs')} ·{' '}
              </>
            )}
            {t(sexLabelKey(patient.sex))}
            {' · '}
            <span className="font-mono">{patient.mrn}</span>
          </div>
        </div>
        {ticket.status === 'recorded' && (
          <Badge tone="info" size="md">
            {t('consulting.alreadyRecorded')}
          </Badge>
        )}
        <Button asChild variant="secondary">
          <Link to={`/patients/${patient.id}`}>
            <Icon name="arrow_up_right" size={16} /> {t('registry.openFullRecord')}
          </Link>
        </Button>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-line px-4 py-3">
          <h3 className="text-[14px] font-semibold tracking-[-0.01em]">{t('consulting.pastVisits')}</h3>
        </div>
        {/* Keyed by patient so an open visit does not stay open across a change
            of patient. */}
        <VisitHistoryList key={patient.id} patientId={patient.id} sex={patient.sex} />
      </Card>
    </>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-[920px] flex-col gap-5">
      <h1 className="text-2xl font-semibold tracking-[-0.02em]">{t('consulting.title')}</h1>
      {children}
    </div>
  );
}

function Message({ title, body, error }: { title: string; body?: string; error?: boolean }) {
  return (
    <Card className="px-6 py-10 text-center" role={error ? 'alert' : undefined}>
      <div className={'text-[15px] font-semibold tracking-[-0.01em] ' + (error ? 'text-destructive' : '')}>
        {title}
      </div>
      {body && <p className="mx-auto mt-1.5 max-w-[380px] text-[13px] text-muted">{body}</p>}
    </Card>
  );
}
